/**
 * ATL-960 — the largest stock quantity BOTH stores can represent.
 *
 * Every stock-affecting quantity the app writes ends up in a `numeric(10,4)` column on the Supabase
 * side: `product_movements.increment`, `product_movements.previous_stock` and
 * `product_store_stock.quantity`. `numeric(10,4)` holds |value| < 10^6; anything at or above that
 * makes Postgres raise `22003 numeric field overflow` inside `create_writeoff_atomic` /
 * `create_supply_atomic`.
 *
 * Firestore has no such ceiling, and the dual-write commits Firestore FIRST. So an unbounded
 * quantity is accepted into the customer's books and only the Supabase leg refuses it — the two
 * stores then disagree, and the side the tills read is the wrong one. That is exactly what happened
 * in production on 2026-08-24 (org `robtRH9b0uwtvOyxJDrY`): a write-off of 1e15 units and a supply
 * of 1e20 units — the latter beyond `Number.MAX_SAFE_INTEGER`, so it cannot even be an
 * exactly-counted quantity — left one product reading 1e20 units in Firestore with no
 * `product_store_stock` row at all in Supabase.
 *
 * The bound is therefore the COLUMN's, not an invented business rule: it is precisely the set of
 * values that can be written to both stores. It REFUSES; it never clamps. Substituting 999,999 for
 * what the user typed would put a number nobody entered into an accounting record.
 *
 * Mirrored on the backend by `maxStorableValue(DECIMAL_10_4)` in
 * `apps/atlas-backend/src/common/utils/numeric-column-range.util.ts`, which is where the DTO bound
 * and the pre-RPC guard both come from.
 */

/** `numeric(10,4)` ceiling: a stored value must be strictly below this. */
export const STOCK_QUANTITY_LIMIT = 1000000

/** Largest storable magnitude — one unit of the last decimal place below the ceiling. */
export const MAX_STOCK_QUANTITY = 999999.9999

/** The column these bounds describe, for user-facing and log messages. */
export const STOCK_QUANTITY_COLUMN = 'numeric(10,4)'

/**
 * True when `value` cannot be stored as a stock quantity.
 *
 * A non-finite value (NaN, ±Infinity) counts as unstorable: over JSON both become `null`, which is
 * indistinguishable from "the field was not sent". Callers that run their own NaN check first will
 * simply never reach this branch.
 */
export function exceedsStockQuantityLimit(value: number): boolean {
  if (!Number.isFinite(value)) return true
  // Postgres rounds to the column's scale before comparing, so a value that rounds UP over the
  // ceiling (999999.99995) really does overflow.
  return Math.abs(Math.round(value * 10000) / 10000) >= STOCK_QUANTITY_LIMIT
}

/**
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * ATL-958 (frontend) — bound the value that is actually SENT, not the value that was TYPED.
 *
 * `exceedsStockQuantityLimit` above is applied by both screens to what the user entered. That is not
 * the number the stock ledger receives, on either path:
 *
 *   • WRITE-OFFS. `saveNewWriteOff` (global/Firebase/inventory.ts) EXPLODES a composite assembled at
 *     sale time into its ingredients and sends `info.quantity * ingredient.quantity` per line. A
 *     composite quantity of 500,000 — comfortably under the ceiling — becomes 1,500,000 on an
 *     ingredient used three per unit, and `create_writeoff_atomic` raises 22003.
 *   • SUPPLIES. `create_supply_atomic` resolves each line's product and AGGREGATES
 *     (`SUM(quantity)`, `SUM(returnQuantity)` `GROUP BY resolve_product_uuid(...)`, ATL-780), so two
 *     600,000 lines naming one product are written as one 1,200,000 movement. Each line passes the
 *     per-line check; the document still overflows.
 *
 * The backend already refuses both (ATL-958, `collectSummedQuantityRejections`) — but with a 400,
 * AFTER the Firestore batch has committed. Firestore has no numeric ceiling and commits FIRST, so a
 * backend-only guard leaves the two stores permanently disagreeing about stock, with the tills
 * reading the wrong side. This is the same reason `exceedsStockQuantityLimit` is called on the
 * screen at all; it just was not applied to the right number.
 *
 * WHAT IS DELIBERATELY NOT CHECKED, so that this leg can never refuse what the backend accepts:
 *  - the supply movement's `increment` (`SUM(quantity) − SUM(returnQuantity)`) and its
 *    `previous_stock` (`stock + SUM(quantity)`). The first is bounded by the two sums whenever both
 *    are non-negative, which every real supply is; the second depends on stock this screen does not
 *    hold. The backend bounds neither, and a frontend that bounded more would produce a refusal the
 *    server would have allowed — a FALSE REFUSAL AT THE TILL, which is worse than the divergence
 *    this fixes.
 *  - a line with no product ref. It stays its OWN group rather than being merged with other
 *    ref-less lines, so its total equals its own value and the caller's per-line check has already
 *    passed on it. The RPC drops such lines outright (`WHERE r.product_id IS NOT NULL`), so
 *    inventing a merged group here could only refuse something the server would write.
 *
 * The grouping key is the product ref the line carries. That is the RPC's key one resolution step
 * earlier: `resolve_product_uuid` maps a Firestore id and a UUID for the same product onto one
 * group, so the server can merge two groups this function keeps apart — never the reverse. Erring
 * that way is deliberate: a group this function misses is still caught by the backend's 400 before
 * anything reaches a numeric column, whereas a group it invents would refuse a legitimate write.
 */

/** One line as the RPC will see it, for `firstUnstorableStockQuantity`. */
export interface StockQuantityLine {
  /** The product ref the line carries — `COALESCE(item.productId, item.id)` on the RPC's side. */
  ref?: string | null
  /** Product name, used only to make the refusal readable. */
  name?: string | null
  /** The quantities the RPC sums SEPARATELY for this product, keyed by field name. */
  values: Record<string, number | null | undefined>
}

/** The (product, field) whose SUM cannot be stored. */
export interface StockQuantityRejection {
  /** The product's name, or `null` when the line carries none. */
  name: string | null
  /** Which summed field overflowed — one of the `fields` passed in. */
  field: string
  /** The per-product total that cannot be stored. */
  total: number
}

/**
 * The first (product, field) whose SUMMED quantity cannot be stored, or `null` when all of them can.
 *
 * Lines are grouped by `ref` in document order — a line with no `ref` is its own group — and each
 * named field is summed within the group, exactly as the RPC sums it. A non-finite value makes its
 * group's total non-finite, which `exceedsStockQuantityLimit` already treats as unstorable.
 */
export function firstUnstorableStockQuantity(
  lines: readonly StockQuantityLine[],
  fields: readonly string[],
): StockQuantityRejection | null {
  const groups: Array<{ name: string | null; totals: Record<string, number> }> = []
  const byRef = new Map<string, number>()
  for (const line of lines) {
    const ref = typeof line.ref === 'string' && line.ref ? line.ref : null
    let index = ref !== null ? byRef.get(ref) : undefined
    if (index === undefined) {
      index = groups.length
      groups.push({ name: line.name ?? null, totals: {} })
      if (ref !== null) byRef.set(ref, index)
    }
    const group = groups[index]
    for (const field of fields) {
      const value = line.values[field]
      // A field the line does not carry contributes 0, matching the RPC's COALESCE(..., 0).
      group.totals[field] = (group.totals[field] ?? 0) + (typeof value === 'number' ? value : 0)
    }
  }
  for (const group of groups) {
    for (const field of fields) {
      const total = group.totals[field] ?? 0
      if (exceedsStockQuantityLimit(total)) return { name: group.name, field, total }
    }
  }
  return null
}
